/**
 * 标签 API 接口
 *
 * @module patient-management/tag/domain
 */

import { get, post, put, del } from "@shared/domain/httpClient";

const BASE_URL = "/tags";

/**
 * 标签 API 响应类型
 */
export interface TagApiResponse {
  _id?: string;
  id?: string;
  name: string;
  category: string;
  /** @deprecated 使用 category */
  type?: string;
  color?: string;
  description?: string;
  parent_id?: string;
  order?: number;
  is_system?: boolean;
  usage_count?: number;
  created_at?: string;
  updated_at?: string;
}

/**
 * 标签关联响应
 */
export interface TagAssociationResponse {
  _id?: string;
  id?: string;
  tag_id: string;
  entity_id: string;
  entity_type: string;
  created_at?: string;
  created_by?: string;
}

/**
 * 创建标签请求
 */
export interface CreateTagRequest {
  name: string;
  category: string;
  color?: string;
  description?: string;
  parent_id?: string;
}

/**
 * 更新标签请求
 */
export interface UpdateTagRequest {
  name?: string;
  color?: string;
  description?: string;
  order?: number;
}

// ==================== API 函数 ====================

/**
 * 获取标签列表
 */
export async function fetchTags(params?: {
  type?: string;
  keyword?: string;
  parent_id?: string;
}): Promise<TagApiResponse[]> {
  const response = await get<
    { items: TagApiResponse[]; total: number } | TagApiResponse[]
  >(`${BASE_URL}/`, { params });
  if (Array.isArray(response)) {
    return response;
  }
  return response?.items ?? [];
}

/**
 * 获取标签详情
 */
export async function fetchTagById(id: string): Promise<TagApiResponse> {
  return get<TagApiResponse>(`${BASE_URL}/${id}`);
}

/**
 * 根据类型获取标签
 */
export async function fetchTagsByType(type: string): Promise<TagApiResponse[]> {
  return get<TagApiResponse[]>(`${BASE_URL}/type/${type}`);
}

/**
 * 搜索标签
 */
export async function searchTags(
  keyword: string,
  limit: number = 20,
): Promise<TagApiResponse[]> {
  return get<TagApiResponse[]>(`${BASE_URL}/search`, {
    params: { keyword, limit },
  });
}

/**
 * 创建标签
 */
export async function createTag(
  data: CreateTagRequest,
): Promise<TagApiResponse> {
  return post<TagApiResponse>(`${BASE_URL}/`, data);
}

/**
 * 更新标签
 */
export async function updateTag(
  id: string,
  data: UpdateTagRequest,
): Promise<TagApiResponse> {
  return put<TagApiResponse>(`${BASE_URL}/${id}`, data);
}

/**
 * 删除标签
 */
export async function deleteTag(id: string): Promise<{ success: boolean }> {
  return del<{ success: boolean }>(`${BASE_URL}/${id}`);
}

/**
 * 给实体添加标签
 */
export async function addTagToEntity(
  tagId: string,
  entityId: string,
  entityType: string,
): Promise<TagAssociationResponse> {
  return post<TagAssociationResponse>(`${BASE_URL}/${tagId}/entities`, {
    entity_id: entityId,
    entity_type: entityType,
  });
}

/**
 * 从实体移除标签
 */
export async function removeTagFromEntity(
  tagId: string,
  entityId: string,
): Promise<{ success: boolean }> {
  return del<{ success: boolean }>(`${BASE_URL}/${tagId}/entities/${entityId}`);
}

/**
 * 获取实体的所有标签
 */
export async function fetchEntityTags(
  entityId: string,
  entityType: string,
): Promise<TagApiResponse[]> {
  return get<TagApiResponse[]>(`${BASE_URL}/entity/${entityType}/${entityId}`);
}

/**
 * 获取使用某标签的所有实体
 */
export async function fetchTaggedEntities(
  tagId: string,
  entityType: string,
): Promise<string[]> {
  const result = await get<{ entity_ids: string[] }>(
    `${BASE_URL}/${tagId}/entities`,
    {
      params: { entity_type: entityType },
    },
  );
  return result.entity_ids;
}

/**
 * 批量给实体添加标签
 */
export async function batchAddTagsToEntity(
  tagIds: string[],
  entityId: string,
  entityType: string,
): Promise<TagAssociationResponse[]> {
  return post<TagAssociationResponse[]>(`${BASE_URL}/batch/add`, {
    tag_ids: tagIds,
    entity_id: entityId,
    entity_type: entityType,
  });
}

/**
 * 批量从实体移除标签
 */
export async function batchRemoveTagsFromEntity(
  tagIds: string[],
  entityId: string,
): Promise<{ success: boolean }> {
  return post<{ success: boolean }>(`${BASE_URL}/batch/remove`, {
    tag_ids: tagIds,
    entity_id: entityId,
  });
}

// ==================== 患者标签 API ====================

export async function addPatientTags(patientId: string, tagNames: string[]) {
  return post<{ success: boolean }>(`${BASE_URL}/patient/${patientId}/add`, {
    tag_names: tagNames,
  });
}

export async function removePatientTags(patientId: string, tagNames: string[]) {
  return post<{ success: boolean }>(`${BASE_URL}/patient/${patientId}/remove`, {
    tag_names: tagNames,
  });
}

export async function getPatientTags(
  patientId: string,
): Promise<{ patient_id: string; tags: TagApiResponse[] }> {
  return get<{ patient_id: string; tags: TagApiResponse[] }>(
    `${BASE_URL}/patient/${patientId}`,
  );
}

// ==================== 病历标签 API ====================

export async function addRecordTags(recordId: string, tagNames: string[]) {
  return post<{ success: boolean }>(`${BASE_URL}/record/${recordId}/add`, {
    tag_names: tagNames,
  });
}

export async function removeRecordTags(recordId: string, tagNames: string[]) {
  return post<{ success: boolean }>(`${BASE_URL}/record/${recordId}/remove`, {
    tag_names: tagNames,
  });
}

export async function getRecordTags(
  recordId: string,
): Promise<{ record_id: string; tags: string[] }> {
  return get<{ record_id: string; tags: string[] }>(
    `${BASE_URL}/record/${recordId}`,
  );
}

export async function searchRecordsByTag(
  tags: string[],
  match: "all" | "any" = "any",
  skip = 0,
  limit = 50,
): Promise<{ items: unknown[]; total: number }> {
  return get<{ items: unknown[]; total: number }>(
    `${BASE_URL}/records-by-tag`,
    {
      params: { tags: tags.join(","), match, skip, limit },
    },
  );
}

// ==================== 按范围获取标签 ====================

export async function fetchTagsByScope(
  scope: "patient" | "record",
): Promise<TagApiResponse[]> {
  const response = await get<
    { items: TagApiResponse[]; total: number } | TagApiResponse[]
  >(`${BASE_URL}/`, { params: { scope } });
  if (Array.isArray(response)) return response;
  return response?.items ?? [];
}
